import _round from 'lodash/round';

interface FormatDeltaInput {
    /**
     * The change between two numbers to format as a string; should be expressed as a percentage.
     * Example:
     * number A: 1
     * number B: 2
     *
     * delta = 200 // a 200% change from A to B
     */
    percentDelta: Maybe<number>;
    /**
     * The threshold above which the label will be formatted as a multiple.
     */
    threshold?: number;
}

/**
 * Return a percentage formatted for display as a delta (change in value). Note that the value is
 * always expressed as a positive number; the consuming code will need to display it appropriately
 * according to whether input.delta is positive or negative.
 *
 * Example:
 *
 * formatDelta({ delta: 200, threshold: 300 }) // { isIncrease: true, label: '200%' }
 *
 * formatDelta({ delta: 320, threshold: 300 }) // { isIncrease: true, label: '3.2x' }
 *
 * @param input - see {@link FormatDeltaInput}
 * @returns the label, or null if input.delta is null
 */
export const formatDelta = ({
    percentDelta,
    threshold = 300,
}: FormatDeltaInput): Maybe<string> => {
    if (percentDelta === null) {
        return null;
    }

    const percentDeltaAbs = Math.abs(percentDelta);

    if (percentDeltaAbs > threshold) {
        return `${_round(percentDeltaAbs / 100, 1)}x`;
    }

    return `${_round(percentDeltaAbs, 0)}%`;
};
